/**
 * Gemeinsame Typen für den SAP-Fileupload.
 *
 * Der Vertrag spiegelt die API von `soff-sap-svc-fileupload-api-v1` wider:
 * `POST {apiBaseUrl}/{aggregateType}/{aggregateId}` mit rohem Binary-Body
 * (exakt eine Datei pro Request; `Content-Length` ist Pflicht, `Content-Type`
 * ergibt sich aus der Datei), den SAP-Parametern als Query-Parameter
 * (inkl. `fileName`) und Bearer-Authentifizierung. Clients mit mehreren
 * Dateien senden einen Request pro Datei. Die Antwort ist ein
 * `SoffApiResponseSingleDto<UploadResponse>` – ein Ergebnis pro Request.
 */

/** Sichtbarkeits-Klassifizierung – entspricht dem `SapVisibility`-Enum des Service. */
export type Visibility = 'ClaasInternal' | 'DealerTypeA' | 'DealerTypeB';

/** Envelope-Typ für Listen-Responses von SOFF-APIs (`SoffApiResponseListDto`). */
export interface SoffApiListResponse<T> {
  data: T[];
}

/** Envelope-Typ für Einzel-Responses von SOFF-APIs (`SoffApiResponseSingleDto`). */
export interface SoffApiSingleResponse<T> {
  data: T;
}

/**
 * Pflicht-Parameter, die als Query-Parameter an den Upload-Endpunkt übergeben
 * werden. Alle Felder sind serverseitig `@NotBlank` / `@NotNull`.
 */
export interface SapUploadParameters {
  visibility: Visibility;
  position: string;
  aggregateType: string;
  aggregateId: string;
  /** Optionale Business-Kategorie; wird als `category`-Query-Parameter gesendet. */
  category?: string;
  /**
   * Optionale freie Tags. Werden als kommaseparierter `tags`-Query-Parameter
   * gesendet; der Service leitet jeden Wert als `SapTag:<value>` weiter.
   */
  tags?: string[];
  /**
   * Optionaler namentlich bekannter Benutzer. Überschreibt den technischen
   * Benutzer aus dem Bearer-Token als `uploadUser`-Query-Parameter.
   */
  uploadUser?: string;
}

/**
 * Ergebnis eines einzelnen Datei-Uploads (eine Antwort pro Request). {@link
 * message} trägt bei Misserfolg den Grund und ist bei Erfolg `null`.
 */
export interface UploadResponse {
  aggregateId: string;
  success: boolean;
  message: string | null;
}

/**
 * Upload-Konfiguration vom Endpunkt `GET {base}/config`. Spiegelt
 * `UploadConfigResponse` von `soff-sap-svc-fileupload-api-v1` wider.
 *
 * - {@link maxFileSizeBytes}: serverseitig ein **Pro-Datei**-Limit in Bytes. Das
 *   Frontend interpretiert diesen Wert jedoch **bewusst als Gesamt-Request-Limit**
 *   (siehe Hinweis unten und {@link UploadConfigResponse} im Backend).
 * - {@link maxFiles}: maximale Anzahl Dateien pro Request (Sicherheits-Limit).
 * - {@link allowedFileTypes}: erlaubte Typen (MIME oder Endung). Leer ⇒ alles
 *   erlaubt, außer Verbotenem.
 * - {@link forbiddenFileTypes}: verbotene Typen (MIME oder Endung) – haben immer
 *   Vorrang vor der Allow-Liste.
 *
 * **Bewusster „Glitch":** {@link maxFileSizeBytes} ist serverseitig pro Datei
 * gemeint, wird im Frontend aber als Gesamtgrenze über alle ausgewählten Dateien
 * durchgesetzt. Damit ist die FE-Grenze strenger als die Server-Grenze – gewollt,
 * weil es keinen serverseitigen Gesamt-Request-Cap gibt.
 */
export interface UploadConfig {
  maxFileSizeBytes: number;
  maxFiles: number;
  allowedFileTypes: string[];
  forbiddenFileTypes: string[];
}

/**
 * Ereignisse während des Uploads einer einzelnen Datei: laufender Fortschritt
 * (`progress` in Prozent, 0–100) gefolgt von genau einem `response`-Ereignis
 * mit dem Ergebnis dieser Datei.
 */
export type UploadEvent =
  | { type: 'progress'; progress: number }
  | { type: 'response'; response: UploadResponse };

/** Status einer einzelnen Datei während und nach dem Upload. */
export type FileUploadStatus = 'pending' | 'uploading' | 'success' | 'error';

/**
 * Fortschritts- bzw. Ergebniszustand pro Datei – wird direkt in der Dateiliste
 * des Uploaders dargestellt.
 */
export interface FileUploadState {
  status: FileUploadStatus;
  /** Upload-Fortschritt in Prozent (0–100). */
  progress: number;
  /** Erfolgs- bzw. Fehlermeldung; `null`/leer wenn nicht relevant. */
  message?: string | null;
}

/** Stabiler Schlüssel zur Zuordnung eines {@link FileUploadState} zu einer Datei. */
export function fileUploadKey(file: File): string {
  return `${file.name}::${file.size}::${file.lastModified}`;
}

// ---------------------------------------------------------------------------
// Datei-Metadaten (GET-Endpunkte)
// ---------------------------------------------------------------------------

/** Aggregat-Referenz einer Datei (`aggregateType:aggregateId`-Semantik). */
export interface FileReferencedAggregate {
  aggregateType: string;
  aggregateId: string;
}

/** Tag einer Datei (`type:value`-Semantik, z.B. `SapPosition:10`). */
export interface FileTag {
  type: string;
  value: string;
}

/**
 * Sichtbarkeits-Eintrag einer Datei. Der Service speichert Sichtbarkeit als
 * `{aggregateType: "SapVisibility", aggregateId: "ClaasInternal"}`.
 */
export interface FileVisibility {
  aggregateType: string;
  aggregateId: string;
}

/** Vollständige Datei-Metadaten, wie sie der `listFiles`-Endpunkt liefert. */
export interface FileMetadata {
  aggregateId: string;
  aggregateType: string;
  fingerprint: string;
  fileUri: string;
  thumbnailUri: string | null;
  fileName: string;
  fileExtension: string;
  mimeType: string;
  fileSize: number;
  description: string | null;
  expiresAt: string | null;
  deletedAt: string | null;
  referencedAggregates: FileReferencedAggregate[];
  visibleFor: FileVisibility[];
  quarantine: boolean;
  tags: FileTag[];
  createdByService: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  updatedByService: string;
}
